import { notifyIfKeyRevoked } from '../services/api'
import { useCallback, useEffect, useState } from 'react'
import { STORAGE_KEYS } from '../constants/config'

// ---------------------------------------------------------------------------
// Direct-fetch data layer for the Platform Admin workspace.
//
// WHY THIS EXISTS (do NOT "simplify" back to the shared axios `api` instance):
// Every admin read GET made through the axios `api` instance inside the
// `/admin/*` route context never settled its react-query loading flag, so the
// Overview/Users/Audit screens sat on a permanent skeleton wall. This was
// verified live, exhaustively: the endpoints return 200 (`/admin/stats` →
// `{"stats":{project_count:73,...}}`), a raw `fetch()` AND a raw
// `XMLHttpRequest` to the exact same URL+token resolve in <1s, and the app's
// own XHR reaches `readyState 4 / status 200 / load` with the full 122-byte
// body — yet the axios promise the queryFn awaits never resolves (and never
// rejects: there is no retry), so `isLoading` is stuck `true` forever. The same
// `getAuditLog` call resolves fine on the project pages; only the admin route
// context hangs. A plain `fetch` in the component's own effect is immune —
// it re-renders via `useState`, not via a react-query observer that never fires.
//
// The auth header mirrors the axios request interceptor (Bearer id-token from
// localStorage). 401 refresh is intentionally NOT replicated here: admin reads
// happen inside a fresh authenticated session and a stale token surfaces as an
// honest error state (retry) rather than a silent hang.
// ---------------------------------------------------------------------------

function apiBase(): string {
  const ep = typeof window !== 'undefined' ? window.INNOVATEFLOW_CONFIG?.API_ENDPOINT : undefined
  return ep ? `${ep}/innovateflow` : ''
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/** One authenticated GET → parsed JSON. Throws on a non-2xx response. */
export async function authedGetJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, { headers: authHeaders(), signal })
  if (res.status === 403) {
    // Story #6490 8.3: a key-status 403 raises the app-wide banner here too.
    try { notifyIfKeyRevoked(403, await res.clone().json()) } catch { /* non-JSON 403 */ }
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as T
}

export interface AuthedFetchState<T> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  /** Re-run the fetch (e.g. after a mutation that changes the data). */
  refetch: () => void
}

/**
 * Fetch `path` (relative to the innovateflow API base) with the current
 * id-token, re-fetching whenever `path` changes or `refetch()` is called.
 * Pass `path = null` to stay idle (e.g. a query that is not yet enabled).
 */
export function useAuthedFetch<T>(path: string | null): AuthedFetchState<T> {
  const [data, setData] = useState<T | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(path != null)
  const [isError, setIsError] = useState(false)
  const [nonce, setNonce] = useState(0)

  const refetch = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    if (path == null) {
      setIsLoading(false)
      return
    }
    const ctrl = new AbortController()
    setIsLoading(true)
    setIsError(false)
    authedGetJson<T>(path, ctrl.signal)
      .then((json) => {
        setData(json)
        setIsLoading(false)
      })
      .catch((err) => {
        if ((err as Error)?.name === 'AbortError') return
        setIsError(true)
        setIsLoading(false)
      })
    return () => ctrl.abort()
  }, [path, nonce])

  return { data, isLoading, isError, refetch }
}

export interface AdminUser {
  user_id: string
  email: string
  role: string
  status: string
  enabled: boolean
  created: string
}

/**
 * Load the full platform user directory, following Cognito's `pagination_token`
 * until exhausted (BUG-147: a single page hid — and made unsearchable — every
 * user past the first). Uses the direct-fetch path for the same reason as
 * useAuthedFetch above.
 */
export function useAdminUsers(): AuthedFetchState<{ users: AdminUser[]; count: number }> {
  const [data, setData] = useState<{ users: AdminUser[]; count: number } | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [nonce, setNonce] = useState(0)
  const refetch = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    const ctrl = new AbortController()
    setIsLoading(true)
    setIsError(false)
    void (async () => {
      try {
        const all: AdminUser[] = []
        let token: string | undefined
        let count = 0
        for (let guard = 0; guard < 200; guard += 1) {
          const page = await authedGetJson<{ users: AdminUser[]; count: number; pagination_token?: string }>(
            token ? `/admin/users?pagination_token=${encodeURIComponent(token)}` : '/admin/users',
            ctrl.signal,
          )
          all.push(...(page.users ?? []))
          count = page.count ?? all.length
          const next = page.pagination_token
          if (!next || next === token) break
          token = next
        }
        setData({ users: all, count })
        setIsLoading(false)
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return
        setIsError(true)
        setIsLoading(false)
      }
    })()
    return () => ctrl.abort()
  }, [nonce])

  return { data, isLoading, isError, refetch }
}
